import React from "react";

/**
 * Help content for the Page Widgets section.
 * Follows Velaro doc notation standards:
 *  - Variables → blue pill
 *  - Values → amber pill
 *  - Code → dark slate
 *  - Steps → numbered colored circles
 */
export default function PageWidgetHelp() {
  return (
    <div className="flex flex-col gap-6 p-2 max-w-3xl text-sm">
      {/* What are Page Widgets */}
      <section>
        <h3 className="font-semibold text-base mb-2">What are Page Widgets?</h3>
        <p className="text-slate-600 leading-relaxed">
          Page Widgets are lightweight, focused engagement tools that slide out on specific pages
          of your website. Unlike the full messenger, each widget does exactly one thing — and
          does it quickly. They use your existing Velaro embed script, so no extra code is
          needed on your site.
        </p>
      </section>

      {/* Widget types */}
      <section>
        <h3 className="font-semibold text-base mb-3">Widget Types</h3>
        <div className="flex flex-col gap-3">
          {[
            {
              icon: "fa-phone",
              color: "text-green-600 bg-green-100",
              name: "Click to Call",
              description:
                "A floating phone button. When clicked, it slides out a panel with your phone number and a call button. Works on mobile with tap-to-call."
            },
            {
              icon: "fa-comment-sms",
              color: "text-blue-600 bg-blue-100",
              name: "Click to Text",
              description:
                "A floating SMS button. Visitors enter their phone number and you follow up via SMS. Routes to your Velaro SMS inbox."
            },
            {
              icon: "fa-comments",
              color: "text-purple-600 bg-purple-100",
              name: "Topic Chat",
              description:
                'A conversational prompt anchored to a page — "Have questions about our pricing? Just ask." Routes to a team or workflow.'
            }
          ].map((w) => (
            <div key={w.name} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${w.color}`}>
                <i className={`fa-solid ${w.icon}`} />
              </div>
              <div>
                <div className="font-medium">{w.name}</div>
                <div className="text-slate-500 mt-0.5">{w.description}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How to deploy */}
      <section>
        <h3 className="font-semibold text-base mb-3">How to Deploy</h3>
        <ol className="flex flex-col gap-3">
          {[
            "Create a widget and configure its type, content, and brand color.",
            "Go to the Embed Code tab and copy the script snippet.",
            "Paste the snippet into your page HTML before </body>, or add it via Google Tag Manager.",
            "Optionally set Display Rules to control which pages show the widget — or install it site-wide and let the rules filter."
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                ["bg-primary", "bg-teal-500", "bg-purple-500", "bg-amber-500"][i]
              }`}>
                {i + 1}
              </span>
              <span className="text-slate-600">{step}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Display rules */}
      <section>
        <h3 className="font-semibold text-base mb-2">Display Rules</h3>
        <p className="text-slate-600 leading-relaxed mb-3">
          Display rules let you control exactly when and where a widget appears. Rules are
          evaluated by{" "}
          <code className="bg-slate-800 text-green-300 rounded px-1.5 py-0.5 text-xs font-mono">
            widget.js
          </code>{" "}
          in the visitor's browser — no server round-trip, no delay.
        </p>
        <div className="bg-slate-50 rounded-lg p-4 flex flex-col gap-2 text-slate-600">
          <div className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-medium">url_contains</span>
            <span>Shows if the page URL contains the text (e.g., <em>/products</em>)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-medium">url_equals</span>
            <span>Shows only on an exact URL match</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-medium">url_matches</span>
            <span>Glob pattern (e.g., <em>/blog/*</em> matches all blog pages)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-medium">always</span>
            <span>Always shows — use for site-wide widgets</span>
          </div>
        </div>
        <p className="text-slate-500 mt-2">
          You can also add a <strong>delay</strong> (show after N seconds on page) or a{" "}
          <strong>scroll trigger</strong> (show after visitor scrolls past a certain percentage).
        </p>
      </section>

      {/* Subscription note */}
      <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-700 flex gap-3">
        <i className="fa-solid fa-star mt-0.5 flex-shrink-0" />
        <div>
          <strong>Page Widgets</strong> are a subscription-gated feature. Contact your account
          manager to enable them for your account.
        </div>
      </section>
    </div>
  );
}
